// Site Manager Service for handling site-specific offer letter configurations
import siteConfigService from './site-config-service'

function normalizeClientName(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
}

/** Loose client match: substring either way after normalize (handles minor naming drift). */
function siteMatchesClient(site, clientFilter) {
  const needle = normalizeClientName(clientFilter)
  if (!needle) return true
  const hay = normalizeClientName(site && site.client)
  if (!hay) return false
  return hay.includes(needle) || needle.includes(hay)
}

function extractSitesArray(response) {
  if (!response) return []
  if (Array.isArray(response.data)) return response.data
  if (Array.isArray(response)) return response
  return []
}

class SiteManager {
  constructor() {
    this.sitesCache = {}
    this.sitesListCache = null
    this.cacheExpiry = 5 * 60 * 1000 // 5 minutes cache
    this.lastFetchTime = null
  }

  // Get all available sites from database
  // clientFilter is matched against offer_table_configs.client (backend ILIKE %filter%).
  async getAvailableSites(clientFilter = null) {
    try {
      const filter = clientFilter ? String(clientFilter).trim() : ''
      const cacheKey = filter || '__all__'
      // Check cache first
      if (this.sitesListCache?.[cacheKey] && this.lastFetchTime && (Date.now() - this.lastFetchTime) < this.cacheExpiry) {
        console.log('📦 [Site Manager] Using cached sites data (cache age:', Math.round((Date.now() - this.lastFetchTime) / 1000), 'seconds)')
        return this.sitesListCache[cacheKey]
      }

      console.log('🔄 [Site Manager] Fetching fresh sites data from database...', {
        clientFilter: filter || null,
      })
      let response = await siteConfigService.getAllSites(
        filter ? { client: filter } : {}
      )
      console.log('📥 [Site Manager] Site config service response:', {
        status: response.status,
        message: response.message,
        dataCount: Array.isArray(response.data) ? response.data.length : 0,
        clientFilter: filter || null,
      })

      // Backend returns: { status: "OK", message: "...", data: [...] }
      if (response.status !== 'OK') {
        throw new Error(`Failed to fetch sites from database: ${response.message || 'Unknown error'}`)
      }

      let sites = extractSitesArray(response)

      // Server ILIKE filter can miss when dashboard name ≠ offer_table_configs.client.
      // Retry without filter and match client-side (and report clear mismatch errors).
      if (sites.length === 0 && filter) {
        console.warn(
          `⚠️ [Site Manager] No active sites for client filter "${filter}". Retrying without server client filter...`,
        )
        response = await siteConfigService.getAllSites({})
        if (response.status !== 'OK') {
          throw new Error(`Failed to fetch sites from database: ${response.message || 'Unknown error'}`)
        }
        const allActive = extractSitesArray(response)
        const matched = allActive.filter(site => siteMatchesClient(site, filter))
        console.log('📥 [Site Manager] Unfiltered active sites:', allActive.length, 'matched client:', matched.length)
        if (matched.length > 0) {
          sites = matched
        } else if (allActive.length > 0) {
          const sampleClients = [...new Set(allActive.map(s => s.client).filter(Boolean))].slice(0, 8)
          throw new Error(
            `No active sites for client: ${filter}. `
            + `Found ${allActive.length} active site(s) for other clients`
            + (sampleClients.length ? ` (e.g. ${sampleClients.join(', ')})` : '')
            + `. Set offer_table_configs.client to match the dashboard name "${filter}".`,
          )
        } else {
          throw new Error(
            `No active sites for client: ${filter}. `
            + 'No active rows in offer_table_configs (check is_active = true).',
          )
        }
      }

      if (sites.length === 0) {
        const emptyMsg = filter
          ? `No active sites for client: ${filter}`
          : 'No active sites found in database. Ensure offer_table_configs has is_active = true rows.'
        throw new Error(emptyMsg)
      }

      console.log('✅ [Site Manager] Processing', sites.length, 'sites from database')
      console.log('📋 [Site Manager] Raw database sites:', sites.map(s => ({
        id: s.id,
        site_code: s.site_code,
        site_name: s.site_name,
        client: s.client,
        location: s.location,
        is_active: s.is_active
      })))

      // Map backend fields to frontend format
      // Backend fields: id, site_code, site_name, client, project_type, location, is_active
      const mappedSites = sites
        .filter(site => site.is_active !== false && site.is_active !== 0 && site.is_active !== 'false')
        .map(site => ({
          id: site.id,
          code: site.site_code,
          name: site.site_name, // ✅ Using site_name from database
          client: site.client || null,
          location: site.location || null,
          projectType: site.project_type || null,
          isActive: site.is_active !== false && site.is_active !== 0 && site.is_active !== 'false'
        }))
        // Remove duplicates by site_code (keep the most recent one)
        .reduce((acc, site) => {
          const existing = acc.find(s => s.code === site.code)
          if (!existing || site.id > existing.id) {
            // Remove existing and add new one
            const filtered = acc.filter(s => s.code !== site.code)
            filtered.push(site)
            return filtered
          }
          return acc
        }, [])

      if (mappedSites.length === 0) {
        const emptyMsg = filter
          ? `No active sites for client: ${filter}`
          : 'No active sites found in database after filtering is_active.'
        throw new Error(emptyMsg)
      }

      if (!this.sitesListCache || typeof this.sitesListCache !== 'object') {
        this.sitesListCache = {}
      }
      this.sitesListCache[cacheKey] = mappedSites

      this.lastFetchTime = Date.now()
      console.log('✅ [Site Manager] Mapped sites from database:', mappedSites.length, 'sites')
      console.log('📋 [Site Manager] Final mapped sites:', mappedSites.map(s => ({
        code: s.code,
        name: s.name, // This is site_name from database
        client: s.client,
        location: s.location
      })))
      return mappedSites
    } catch (error) {
      console.error('Error fetching available sites:', error)
      // Avoid double-prefixing when we already built a clear message
      const msg = error && error.message ? error.message : 'Unknown error'
      if (/^Failed to (fetch|get) sites/i.test(msg) || /^No active sites/i.test(msg)) {
        throw new Error(msg)
      }
      throw new Error(`Failed to fetch sites from database: ${msg}`)
    }
  }

  // Get site configuration by code from database
  async getSiteConfig(siteCode) {
    try {
      // Check cache first
      if (this.sitesCache[siteCode] && this.lastFetchTime && (Date.now() - this.lastFetchTime) < this.cacheExpiry) {
        return this.sitesCache[siteCode]
      }

      const response = await siteConfigService.getSiteByCode(siteCode)
      
      // Backend returns: { status: "OK", message: "...", data: {...} }
      if (response.status === 'OK' && response.data) {
        // Site-by-code can omit large branding images; prefer full GET-by-id.
        let siteData = response.data
        if (siteData && siteData.id != null) {
          try {
            const fullRes = await siteConfigService.getSiteById(siteData.id)
            if (fullRes && fullRes.status === 'OK' && fullRes.data) {
              siteData = fullRes.data
            }
          } catch (e) {
            console.warn('[Site Manager] GET-by-id after site-by-code failed:', e && e.message)
          }
        }
        
        // Parse metadata (REQUIRED - contains HTML template and images)
        // Metadata is stored as JSON in the database
        let metadata = null
        if (siteData.metadata) {
          try {
            if (typeof siteData.metadata === 'string') {
              // Parse JSON string to object
              metadata = JSON.parse(siteData.metadata)
            } else if (typeof siteData.metadata === 'object' && siteData.metadata !== null) {
              // Already an object (might be parsed by ORM/backend)
              metadata = siteData.metadata
            }
            console.log(`✅ [Site Manager] Successfully parsed metadata for ${siteCode}`)
            console.log(`📋 [Site Manager] Metadata keys:`, Object.keys(metadata || {}))
            if (metadata && metadata.html) {
              console.log(`📋 [Site Manager] metadata.html type:`, typeof metadata.html)
              if (typeof metadata.html === 'object') {
                console.log(`📋 [Site Manager] metadata.html keys:`, Object.keys(metadata.html))
              }
            }
          } catch (metadataError) {
            console.error(`❌ [Site Manager] Error parsing metadata JSON for ${siteCode}:`, metadataError)
            console.error('❌ [Site Manager] Raw metadata value:', siteData.metadata)
            throw new Error(`Invalid metadata format for site: ${siteCode}. Error: ${metadataError.message}`)
          }
        } else {
          throw new Error(`Metadata field is required but not found for site: ${siteCode}. Please ensure metadata is configured in the database.`)
        }
        
        // Validate that HTML template exists in metadata
        if (!metadata.html) {
          throw new Error(`HTML template not found in metadata for site: ${siteCode}. Please ensure metadata.html is configured in the database.`)
        }
        
        // Configuration field is now optional (not used for replacements)
        // Parse it only if it exists, but don't require it
        let config = {}
        if (siteData.configuration) {
          try {
            if (typeof siteData.configuration === 'string') {
              config = JSON.parse(siteData.configuration)
            } else if (siteData.configuration) {
              config = siteData.configuration
            }
            console.log(`✅ [Site Manager] Configuration found for ${siteCode} (optional)`)
          } catch (parseError) {
            console.warn(`⚠️ [Site Manager] Error parsing configuration JSON for ${siteCode} (optional, continuing):`, parseError)
            config = {}
          }
        } else {
          console.log(`ℹ️ [Site Manager] No configuration field for ${siteCode} (not required, using metadata only)`)
        }
        
        // Attach metadata to config object
        config.metadata = metadata
        
        // Check if HTML is stored directly in the database response (separate html column)
        // The html field might be at the root level of siteData, not in configuration JSON
        if (siteData.html) {
          // HTML can be stored as:
          // 1. Direct string: siteData.html = "<html>..."
          // 2. JSON object with language keys: siteData.html = { "en": "<html>...", "hi": "<html>..." }
          if (typeof siteData.html === 'string') {
            config.html = siteData.html
          } else if (typeof siteData.html === 'object' && siteData.html !== null) {
            // If it's an object, it might have language keys
            config.html = siteData.html
          }
          console.log(`HTML template found directly in database response for ${siteCode}`)
        }
        
        // Cache the result
        this.sitesCache[siteCode] = config
        this.lastFetchTime = Date.now()
        console.log(`Successfully loaded site config for ${siteCode} from database`)
        console.log(`HTML available in config:`, config.html ? 'Yes' : 'No')
        return config
      }
      
      // Site configuration not found in database
      throw new Error(`Site configuration not found in database for code: ${siteCode}. Please ensure the site is configured in the database.`)
    } catch (error) {
      console.error(`Error fetching site config for ${siteCode}:`, error)
      throw new Error(`Failed to fetch site configuration for ${siteCode} from database: ${error.message}`)
    }
  }

  // Get site configuration by state name
  async getSiteConfigByState(stateName) {
    const stateMap = {
      'Assam': 'assam',
      'Bihar': 'bihar', 
      'Gujarat': 'gujarat',
      'Maharashtra': 'maharashtra',
      'Uttar Pradesh': 'up',
      'UP': 'up'
    }
    
    const siteCode = stateMap[stateName]
    return siteCode ? await this.getSiteConfig(siteCode) : null
  }

  // Get site code from state name
  getSiteCodeFromState(stateName) {
    const stateMap = {
      'Assam': 'assam',
      'Bihar': 'bihar',
      'Gujarat': 'gujarat', 
      'Maharashtra': 'maharashtra',
      'Uttar Pradesh': 'up',
      'UP': 'up'
    }
    
    return stateMap[stateName] || 'assam' // Default to assam
  }

  // Get available languages for a site
  async getAvailableLanguages(siteCode) {
    const config = await this.getSiteConfig(siteCode)
    if (!config) return ['en']
    
    return Object.keys(config.offerLetter || {})
  }

  // Get site-specific offer letter configuration
  async getOfferLetterConfig(siteCode, language = 'en') {
    const config = await this.getSiteConfig(siteCode)
    if (!config) {
      throw new Error(`Site configuration not found for site: ${siteCode}. Please ensure the site is configured in the database.`)
    }
    
    // Validate that metadata exists (REQUIRED)
    if (!config.metadata) {
      throw new Error(`Metadata field is required but not found for site: ${siteCode}. Please ensure metadata is configured in the database.`)
    }
    
    // Get HTML template from metadata.html (REQUIRED)
    const getHtmlTemplate = () => {
      if (!config.metadata || !config.metadata.html) {
        throw new Error(`HTML template not found in metadata.html for site: ${siteCode}. Please ensure metadata.html is configured in the database.`)
      }
      
      const html = config.metadata.html
      
      // If HTML is stored as an object with language keys
      if (typeof html === 'object' && html !== null) {
        const langHtml = html[language] || html['en']
        if (langHtml && typeof langHtml === 'string') {
          console.log(`✅ [Site Manager] Found HTML in metadata.html[${language || 'en'}]`)
          return langHtml
        }
        throw new Error(`HTML template not found in metadata.html for language '${language}' or 'en' for site: ${siteCode}`)
      }
      
      // If HTML is stored as a direct string
      if (typeof html === 'string') {
        console.log(`✅ [Site Manager] Found HTML as direct string in metadata.html`)
        return html
      }
      
      throw new Error(`Invalid HTML template format in metadata.html for site: ${siteCode}. Expected string or object with language keys.`)
    }
    
    // Build the return object - only metadata is needed (no configuration column)
    const result = {
      language: language,
      metadata: config.metadata, // All metadata (images, etc.)
      html: getHtmlTemplate() // HTML template from metadata.html
    }
    
    console.log(`✅ [Site Manager] Offer letter config loaded for ${siteCode} (metadata only, no configuration column required)`)
    return result
  }

  // Get site-specific offer letter configuration by job title
  // This method fetches the configuration entry that matches both siteCode and jobTitle
  async getOfferLetterConfigByJobTitle(siteCode, jobTitle, language = 'en') {
    if (!jobTitle) {
      throw new Error('Job title is required to fetch configuration')
    }

    // Get site configuration filtered by jobTitle
    const response = await siteConfigService.getSiteByCodeAndJobTitle(siteCode, jobTitle)
    
    if (response.status !== 'OK' || !response.data) {
      throw new Error(`Site configuration not found for site: ${siteCode} with jobTitle: ${jobTitle}. Please ensure the configuration is set up in the database.`)
    }

    // Site-by-code list payloads often omit/truncate large branding images
    // (Offer Configure edits via GET-by-id for the same reason). Always re-fetch
    // the matched row by id so generate sees metadata.images.signature / stamp.
    let siteData = response.data
    if (siteData && siteData.id != null) {
      try {
        const fullRes = await siteConfigService.getSiteById(siteData.id)
        if (fullRes && fullRes.status === 'OK' && fullRes.data) {
          siteData = fullRes.data
          console.log(
            `✅ [Site Manager] Re-fetched full offer config id=${siteData.id} for stamp/images`,
          )
        }
      } catch (fullFetchErr) {
        console.warn(
          '[Site Manager] GET-by-id fallback failed; using site-list metadata (stamp may be missing):',
          fullFetchErr && fullFetchErr.message,
        )
      }
    }

    // Parse metadata (REQUIRED - contains HTML template and images)
    let metadata = null
    if (siteData.metadata) {
      try {
        if (typeof siteData.metadata === 'string') {
          metadata = JSON.parse(siteData.metadata)
        } else if (typeof siteData.metadata === 'object' && siteData.metadata !== null) {
          metadata = siteData.metadata
        }
        console.log(`✅ [Site Manager] Successfully parsed metadata for ${siteCode} with jobTitle: ${jobTitle}`)
      } catch (metadataError) {
        console.error(`❌ [Site Manager] Error parsing metadata JSON for ${siteCode}:`, metadataError)
        throw new Error(`Invalid metadata format for site: ${siteCode} with jobTitle: ${jobTitle}. Error: ${metadataError.message}`)
      }
    } else {
      throw new Error(`Metadata field is required but not found for site: ${siteCode} with jobTitle: ${jobTitle}. Please ensure metadata is configured in the database.`)
    }

    // Validate that HTML template exists in metadata
    if (!metadata.html) {
      throw new Error(`HTML template not found in metadata for site: ${siteCode} with jobTitle: ${jobTitle}. Please ensure metadata.html is configured in the database.`)
    }

    // Get HTML template from metadata.html
    const getHtmlTemplate = () => {
      const html = metadata.html
      
      // If HTML is stored as an object with language keys
      if (typeof html === 'object' && html !== null) {
        const langHtml = html[language] || html['en']
        if (langHtml && typeof langHtml === 'string') {
          console.log(`✅ [Site Manager] Found HTML in metadata.html[${language || 'en'}] for jobTitle: ${jobTitle}`)
          return langHtml
        }
        throw new Error(`HTML template not found in metadata.html for language '${language}' or 'en' for site: ${siteCode} with jobTitle: ${jobTitle}`)
      }
      
      // If HTML is stored as a direct string
      if (typeof html === 'string') {
        console.log(`✅ [Site Manager] Found HTML as direct string in metadata.html for jobTitle: ${jobTitle}`)
        return html
      }
      
      throw new Error(`Invalid HTML template format in metadata.html for site: ${siteCode} with jobTitle: ${jobTitle}. Expected string or object with language keys.`)
    }

    // Build the return object
    const result = {
      language: language,
      metadata: metadata, // All metadata (images, etc.)
      html: getHtmlTemplate() // HTML template from metadata.html
    }

    console.log(`✅ [Site Manager] Offer letter config loaded for ${siteCode} with jobTitle: ${jobTitle}`)
    return result
  }

  // Get site options for dropdown
  async getSiteOptions(clientFilter = null) {
    try {
      console.log('🔍 [Site Manager] Getting site options for dropdown...', {
        clientFilter: clientFilter || null,
      })
      const sites = await this.getAvailableSites(clientFilter)
      console.log('📥 [Site Manager] Available sites:', sites.length)

      // Ensure we're using site_name from database
      const options = sites
        .filter(site => site.isActive !== false)
        .map(site => ({
          value: site.code,
          text: site.location ? `${site.name} (${site.location})` : site.name // ✅ site.name comes from site_name column
        }))
        .sort((a, b) => a.text.localeCompare(b.text)) // Sort alphabetically by site name

      console.log('✅ [Site Manager] Site options for dropdown:', options)
      console.log('📊 [Site Manager] Data source: DATABASE ✅')
      return options
    } catch (error) {
      console.error('❌ [Site Manager] Error getting site options:', error)
      const msg = error && error.message ? error.message : 'Unknown error'
      // Keep mismatch / empty-client / already-prefixed messages readable in the toast
      if (
        /^No active sites/i.test(msg)
        || /^Failed to (fetch|get) sites/i.test(msg)
        || /^Failed to get site options/i.test(msg)
      ) {
        throw new Error(msg)
      }
      throw new Error(`Failed to get site options from database: ${msg}`)
    }
  }

  // Clear cache (useful after updates)
  clearCache() {
    this.sitesCache = {}
    this.sitesListCache = {}
    this.lastFetchTime = null
  }

  // Get language options
  getLanguageOptions() {
    return [
      { value: 'en', text: 'English' },
      { value: 'hi', text: 'Hindi' }
    ]
  }

  // Validate site and language combination
  async validateSiteLanguage(siteCode, language) {
    const config = await this.getSiteConfig(siteCode)
    if (!config) return false
    
    return config.offerLetter && config.offerLetter[language]
  }
}

export default new SiteManager()
